import type {
  InspectionMessage,
  InspectionSession,
} from '../../services/roomInspectorApi';
import {
  isProcessMessage,
  isThinking,
  isTool,
  messageText,
  type TranscriptTurnBlock,
} from './groupMessages.ts';

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function isCompactWork(message: InspectionMessage): boolean {
  if (message.role === 'user') return false;
  const payload = record(message.payload);
  const semantics = record(message.semantics);
  return (
    (semantics.noticeCommand || payload.noticeCommand) === 'compact' &&
    ['running', 'completed', 'failed', 'canceled'].includes(
      String(semantics.noticeCommandStatus || payload.noticeCommandStatus),
    )
  );
}

export function isBlockingMessage(message: InspectionMessage): boolean {
  if (message.role === 'user') return false;
  const payload = record(message.payload);
  if (message.kind === 'error' || payload.kind === 'agent_visible_error')
    return true;
  return (
    !isCompactWork(message) &&
    (payload.kind === 'agent_system_notice' ||
      (!['text', 'message', 'collaboration'].includes(message.kind) &&
        !isTool(message.kind) &&
        !isThinking(message.kind)))
  );
}

function isAssistantText(message: InspectionMessage): boolean {
  const payload = record(message.payload);
  const semantics = record(message.semantics);
  return (
    ['assistant', 'agent'].includes(message.role) &&
    ['text', 'message'].includes(message.kind) &&
    messageText(message.payload).trim() !== '' &&
    !isBlockingMessage(message) &&
    payload.kind !== 'agent_system_notice' &&
    !semantics.noticeCommand &&
    !payload.noticeCommand
  );
}

function isFinalCandidate(message: InspectionMessage): boolean {
  const payload = record(message.payload);
  const status = String(
    message.status ||
      payload.status ||
      payload.streamState ||
      payload.messageStreamState ||
      '',
  )
    .trim()
    .toLowerCase();
  return (
    isAssistantText(message) &&
    ![
      'active',
      'running',
      'streaming',
      'working',
      'inprogress',
      'in_progress',
      'pending',
      'waiting',
    ].includes(status)
  );
}

export function hasTurnError(value: unknown): boolean {
  return Boolean(
    value && (typeof value !== 'object' || Object.keys(value).length > 0),
  );
}

// Match origin/main's agentMessageFinalTextProjection and Turn work sections.
// Settlement anchors are authoritative, including an unresolved/missing one.
// Only legacy Turns without anchor facts use their last settled text reply.
export function turnPresentation(
  block: TranscriptTurnBlock,
  session: InspectionSession,
  allMessages: InspectionMessage[] = block.messages,
  hasMoreHistory = false,
) {
  const settled =
    block.turn?.phase === 'settled' &&
    block.turn.outcome === 'completed' &&
    !(
      session.activeTurnId === block.turnId &&
      session.activeTurnPhase !== 'settled'
    );
  let final: InspectionMessage | undefined;
  if (settled && block.turnId) {
    const command = block.turn?.completedCommand;
    const anchor = command?.finalAssistantMessageId?.trim();
    const candidates = allMessages.filter(
      (message) => message.turnId === block.turnId && isFinalCandidate(message),
    );
    final = anchor
      ? candidates.find((message) => message.messageId === anchor)
      : command?.finalAssistantMessageResolved !== undefined
        ? undefined
        : candidates[candidates.length - 1];
  }
  const finalInBlock = block.messages.some(
    (message) => final && message.messageId === final.messageId,
  );
  const anchor = block.turn?.completedCommand?.finalAssistantMessageId?.trim();
  const awaitingFinal = Boolean(
    hasMoreHistory &&
      settled &&
      anchor &&
      block.turn?.completedCommand?.finalAssistantMessageResolved === true &&
      !allMessages.some((message) => message.messageId === anchor),
  );
  const collapseReplies =
    ((finalInBlock && !block.messages.some(isBlockingMessage)) ||
      awaitingFinal) &&
    !hasTurnError(block.turn?.error) &&
    session.imported !== true;
  const workMessages = block.messages.filter(
    (message) =>
      !isBlockingMessage(message) &&
      (isProcessMessage(message) ||
        isCompactWork(message) ||
        (collapseReplies &&
          isAssistantText(message) &&
          message.messageId !== final?.messageId)),
  );
  return {
    finalMessageId: finalInBlock ? final?.messageId : undefined,
    awaitingFinal: awaitingFinal && collapseReplies,
    workMessages,
    workIds: new Set(workMessages.map((message) => message.messageId)),
    replyCount: workMessages.filter(isAssistantText).length,
    eventCount: workMessages.filter(
      (message) =>
        !isTool(message.kind) &&
        !isThinking(message.kind) &&
        !isAssistantText(message),
    ).length,
  };
}
